import { Injectable } from '@angular/core';
import { Client, CognitoUser, CognitoUserPool, Section } from '@mira-avant/mtlibs';
import { BehaviorSubject } from 'rxjs';
import { NavigationService } from './navigation.service';
import { Course, Unit, Lesson, Activity, Content } from '@mira-avant/mtlibs';
import { ClientService } from './client.service';
import { CourseService } from './course.service';
import { UnitService } from './unit.service';
import { LessonService } from './lesson.service';
import { ActivityService } from './activity.service';
import { ContentService } from './content.service';
import { CognitoService } from './cognito.service';
import { SectionService } from './section.service';
import { AuthService } from './auth.service';

@Injectable({
	providedIn: 'root',
})
export class AdminService {
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public selectedClient$: BehaviorSubject<Client | undefined> = new BehaviorSubject<Client | undefined>(undefined);
	public selectedSection$: BehaviorSubject<Section | undefined> = new BehaviorSubject<Section | undefined>(undefined);
	public selectedCourse$: BehaviorSubject<Course | undefined> = new BehaviorSubject<Course | undefined>(undefined);
	public selectedUnit$: BehaviorSubject<Unit | undefined> = new BehaviorSubject<Unit | undefined>(undefined);
	public selectedLesson$: BehaviorSubject<Lesson | undefined> = new BehaviorSubject<Lesson | undefined>(undefined);
	public selectedActivity$: BehaviorSubject<Activity | undefined> = new BehaviorSubject<Activity | undefined>(
		undefined
	);
	public selectedContent$: BehaviorSubject<Content | undefined> = new BehaviorSubject<Content | undefined>(undefined);
	public selectedUserPool$: BehaviorSubject<CognitoUserPool | undefined> = new BehaviorSubject<
		CognitoUserPool | undefined
	>(undefined);
	public selectedUser$: BehaviorSubject<CognitoUser | undefined> = new BehaviorSubject<CognitoUser | undefined>(
		undefined
	);
	public lowestSelectedUuid$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

	constructor(
		private nav: NavigationService,
		private authService: AuthService,
		private clientService: ClientService,
		private sectionService: SectionService,
		private courseService: CourseService,
		private unitService: UnitService,
		private lessonService: LessonService,
		private activityService: ActivityService,
		private contentService: ContentService,
		public cognitoService: CognitoService
	) {
		this.authService.user$.subscribe(user => {
			this.nav.matrixParams$.subscribe(async (matrixParams: { [key: string]: string }) => {
				await this.withLoading(async () => {
					//These can be watched by all users
					const promises = [
						this.setSelectedClient$(matrixParams['clientUuid']),
						this.setSelectedSection$(matrixParams['sectionUuid']),
						this.setSelectedCourse$(matrixParams['courseUuid']),
						this.setSelectedUnit$(matrixParams['unitUuid']),
						this.setSelectedLesson$(matrixParams['lessonUuid']),
						this.setSelectedActivity$(matrixParams['activityUuid']),
						this.setSelectedContent$(matrixParams['contentUuid']),
					];

					//Only fetch user pool and user if the user is an admin
					if (await this.authService.isAdmin$()) {
						promises.push(this.setSelectedUserPool$(matrixParams['userPoolUuid']));
						promises.push(this.setSelectedUser$(matrixParams['userPoolUuid'], matrixParams['cognitoUuid']));
					}

					await Promise.all(promises);

					this.updateLowestSelectedUuid(matrixParams);
				});
			});
		});
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	private async setSelectedClient$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedClient$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const client = await this.clientService.get$(uuid);
			await this.selectedClient$.next(client);
			await this.courseService.onClientChange$(uuid);
		} else {
			this.selectedClient$.next(undefined);
			await this.courseService.onClientChange$(undefined);
		}
	}

	private async setSelectedSection$(uuid: string | undefined): Promise<void> {
		if (this.selectedSection$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const section = await this.sectionService.get$(uuid, true);
			await this.selectedSection$.next(section);
		} else {
			this.selectedSection$.next(undefined);
		}
	}

	private async setSelectedCourse$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedCourse$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const course = await this.courseService.get$(uuid);
			await this.selectedCourse$.next(course);
			await this.unitService.onCourseChange$(uuid);
		} else {
			this.selectedCourse$.next(undefined);
		}
	}

	private async setSelectedUnit$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedUnit$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const unit = await this.unitService.get$(uuid);
			await this.selectedUnit$.next(unit);
			await this.lessonService.onUnitChange$(uuid);
		} else {
			this.selectedUnit$.next(undefined);
		}
	}

	private async setSelectedLesson$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedLesson$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const lesson = await this.lessonService.get$(uuid);
			await this.selectedLesson$.next(lesson);
			await this.activityService.onLessonChange$(uuid);
		} else {
			this.selectedLesson$.next(undefined);
		}
	}

	private async setSelectedActivity$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedActivity$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const activity = await this.activityService.get$(uuid);
			await this.selectedActivity$.next(activity);
			await this.contentService.getByActivity$(uuid);
		} else {
			this.selectedActivity$.next(undefined);
		}
	}

	private async setSelectedContent$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedContent$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const content = await this.contentService.get$(uuid);
			this.selectedContent$.next(content);
		} else {
			this.selectedContent$.next(undefined);
		}
	}

	private async setSelectedUserPool$(uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedUserPool$.value?.id === uuid) {
			return;
		}

		const allPools: CognitoUserPool[] = await this.cognitoService.listUserPools();

		if (uuid) {
			const pool = allPools.find(p => p.id === uuid);

			if (pool) {
				await this.selectedUserPool$.next(pool);
				await this.cognitoService.listUsers(uuid);
			}
		} else {
			this.selectedUserPool$.next(undefined);
		}
	}

	private async setSelectedUser$(poolUuid: string, uuid: string | undefined): Promise<void> {
		//If the uuid is the same as the current selection, don't fetch it again
		if (this.selectedUser$.value?.uuid === uuid) {
			return;
		}

		if (uuid) {
			const user: CognitoUser = await this.cognitoService.getUserByCognitoUuid(poolUuid, uuid);

			if (user) {
				this.selectedUser$.next(user);
			}
		} else {
			this.selectedUser$.next(undefined);
		}
	}

	private updateLowestSelectedUuid(matrixParams: { [key: string]: string }): void {
		this.lowestSelectedUuid$.next(undefined);

		this.lowestSelectedUuid$.next(
			matrixParams['contentUuid'] ||
				matrixParams['activityUuid'] ||
				matrixParams['lessonUuid'] ||
				matrixParams['unitUuid'] ||
				matrixParams['courseUuid'] ||
				//Unsure if we need these.
				//matrixParams['locationUuid'] ||
				//matrixParams['sectionUuid'] ||
				matrixParams['clientUuid']
		);
	}
}
